import { useEffect, useState } from 'react';

/**
 * Tells you when a new build has been deployed under an open tab.
 *
 * Nothing here reloads on its own. Someone half-way through typing a salary
 * sheet should not have the page pulled from under them; the banner waits and
 * they reload when it suits.
 *
 * How it knows: the build writes its entry-bundle filename to /version.txt,
 * which is content-hashed, so it changes exactly when the app changes and never
 * otherwise. The value read at startup IS this tab's version - it was fetched
 * moments after the bundle it describes. Later reads that differ mean a deploy
 * landed while this tab was sitting there.
 *
 * A failed check is silence, not a banner. Being briefly offline is not a new
 * version, and a "reload" prompt shown to someone with no connection is a
 * prompt to lose their work.
 */
const VERSION_URL = '/version.txt';
const EVERY_MS = 5 * 60_000;

async function readVersion(): Promise<string | null> {
  try {
    const r = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!r.ok) return null;
    const t = (await r.text()).trim();
    // Guard against the SPA fallback: a missing version.txt returns index.html
    // with a 200, and treating that as a version would flag a new build on
    // every check.
    if (!t || t.length > 120 || t.includes('<')) return null;
    return t;
  } catch {
    return null;
  }
}

export function NewVersionBanner() {
  const [mine, setMine] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void readVersion().then((v) => { if (alive) setMine(v); });
    const id = setInterval(async () => {
      const v = await readVersion();
      if (alive && v) setFresh(v);
    }, EVERY_MS);
    // A tab brought back to the front is the moment someone is about to use it,
    // and the likeliest moment for it to be stale.
    const onFocus = async () => {
      const v = await readVersion();
      if (alive && v) setFresh(v);
    };
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  if (!mine || !fresh || fresh === mine) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 end-4 z-[100] max-w-sm rounded-lg border border-border bg-card shadow-lg px-4 py-3 flex items-center gap-3"
    >
      <div className="text-sm">
        <div className="font-medium">A new version is available</div>
        <div className="text-xs text-muted-foreground">
          This tab is running an older build. Reload when you are ready.
        </div>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="shrink-0 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm"
      >
        Reload
      </button>
      {/* Dismiss only silences the banner for this tab: setting `mine` to the
          new version means it reappears on the NEXT deploy, not this one. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setMine(fresh)}
        className="shrink-0 text-muted-foreground hover:text-foreground text-lg leading-none"
      >
        ×
      </button>
    </div>
  );
}
